import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { useDismissOnOutside } from '../lib/useDismissOnOutside';

export interface MultiSelectOption { value: string; label: string; hint?: ReactNode }

/**
 * A compact multi-select: a button that sums up the choice and opens a popover with checkboxes, All and
 * None, and a search box once there are more than `searchFrom` options. Escape closes it and returns
 * focus to the button; the arrow keys move between the search box and the checkboxes.
 */
export function MultiSelect({ options, selected, onChange, summary, label, testId, searchFrom = 8 }: {
  options: MultiSelectOption[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  /** The button's text for the current choice. */
  summary: ReactNode;
  /** What is chosen (the popover's accessible name). */
  label: string;
  testId?: string;
  searchFrom?: number;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const anchor = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const popoverId = useId();
  useDismissOnOutside(anchor, open, () => setOpen(false));
  const searchable = options.length > searchFrom;
  const q = query.trim().toLowerCase();
  const shown = q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;

  useEffect(() => {
    if (!open) return;
    setQuery('');
    // Focus goes into the popover: the search box, or the first checkbox.
    panel.current?.querySelector<HTMLElement>('input')?.focus();
  }, [open]);

  const close = () => {
    setOpen(false);
    button.current?.focus();
  };
  const toggle = (v: string) => {
    const n = new Set(selected);
    if (n.has(v)) n.delete(v);
    else n.add(v);
    onChange(n);
  };
  // All and None act on what the search shows, so a search can pick a group at once.
  const setShown = (on: boolean) => {
    const n = new Set(selected);
    for (const o of shown) if (on) n.add(o.value); else n.delete(o.value);
    onChange(n);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close();
      return;
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const inputs = [...(panel.current?.querySelectorAll<HTMLInputElement>('input') ?? [])];
    const i = inputs.indexOf(document.activeElement as HTMLInputElement);
    if (i < 0 || !inputs.length) return;
    e.preventDefault();
    inputs[(i + (e.key === 'ArrowDown' ? 1 : -1) + inputs.length) % inputs.length]!.focus();
  };

  return (
    <div className="menu-anchor multi-select" ref={anchor}>
      <button
        ref={button} type="button" className="btn sm multi-select-button" aria-haspopup="dialog" aria-expanded={open} aria-controls={open ? popoverId : undefined}
        onClick={() => setOpen(!open)} onKeyDown={(e) => { if (e.key === 'ArrowDown' && !open) { e.preventDefault(); setOpen(true); } }} data-testid={testId}
      >
        <span className="multi-select-summary">{summary}</span><ChevronDown size={13} aria-hidden />
      </button>
      {open && (
        <div id={popoverId} ref={panel} className="popover multi-select-popover" role="dialog" aria-label={label} onKeyDown={onKeyDown} data-testid={testId && `${testId}-menu`}>
          {searchable && (
            <div className="multi-select-search">
              <Search size={13} aria-hidden />
              <input className="input" type="search" placeholder="Search" value={query} onChange={(e) => setQuery(e.target.value)} aria-label={`Search ${label.toLowerCase()}`} data-testid={testId && `${testId}-search`} />
            </div>
          )}
          <div className="multi-select-bulk">
            <button type="button" className="btn ghost sm" onClick={() => setShown(true)} data-testid={testId && `${testId}-all`}>All</button>
            <button type="button" className="btn ghost sm" onClick={() => setShown(false)} data-testid={testId && `${testId}-none`}>None</button>
            <span className="faint">{selected.size} of {options.length}</span>
          </div>
          <ul>
            {shown.map((o) => (
              <li key={o.value}>
                <label className="toggle-row" data-testid={testId && `${testId}-option`} data-value={o.value} data-label={o.label}>
                  <input type="checkbox" checked={selected.has(o.value)} onChange={() => toggle(o.value)} />
                  <span>{o.label}{o.hint}</span>
                </label>
              </li>
            ))}
            {!shown.length && <li className="muted">Nothing matches “{query}”.</li>}
          </ul>
        </div>
      )}
    </div>
  );
}
